import React, { useState } from 'react';
import { Plus, Trash2, Upload, Scale, FileText, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { MAX_COMPARE } from '../../hooks/useDesignCompare';
import { resolveImageUrl } from '../../lib/imageCache';
import { currentSource } from '../../lib/consultSource';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROWS = ['Mount', 'Size', 'Control', 'Roll direction', 'Bracket', 'Extras', 'Installation', 'Room'];

// Side-by-side comparison of up to three saved configurations with quote request.
export const CompareDesignsDialog = ({ open, onOpenChange, designs, onAddCurrent, canAddCurrent, onLoad, onRemove }) => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });

  const differs = (row) => new Set(designs.map((d) => d.rows?.[row] || '—')).size > 1;
  const cheapest = designs.length > 1 ? Math.min(...designs.map((d) => (d.total_value ?? Infinity))) : null;

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const fabrics = designs.map((d) => ({
        id: d.fabric?.color_id || d.fabric?.id || d.id,
        name: d.fabric?.name || 'Unknown',
        brand_name: d.fabric?.brand_name || '',
        collection_name: d.fabric?.collection_name || '',
        hex_color: d.fabric?.hex || d.fabric?.hex_color || '',
        image_url: d.fabric?.image_url || '',
      }));
      const designPayloads = designs.map((d) => ({
        fabric: { name: d.fabric?.name, hex: d.fabric?.hex || d.fabric?.hex_color },
        rows: d.rows || {},
        total: d.total,
        share_url: d.share_url || '',
      }));
      await axios.post(`${API_URL}/fabric-quote-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        window_count: designs.length,
        ...currentSource('Compare designs: Request Quote'),
        fabrics,
        designs: designPayloads,
      });
      toast.success('Quote request submitted! We\'ll be in touch shortly.');
      setShowQuoteForm(false);
      setForm({ name: '', email: '', phone: '', notes: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setShowQuoteForm(false); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl" data-testid="compare-designs-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Scale className="w-5 h-5 text-amber-600" /> Compare designs</DialogTitle>
          <DialogDescription>Save up to {MAX_COMPARE} configurations and weigh fabric, options and price side by side. Saved in this browser only.</DialogDescription>
        </DialogHeader>

        {designs.length === 0 ? (
          <div className="py-10 text-center text-neutral-500" data-testid="compare-empty">
            <p>No designs saved yet.</p>
            <Button onClick={onAddCurrent} disabled={!canAddCurrent} className="mt-4 bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="compare-add-current-empty">
              <Plus className="w-4 h-4" /> Add current design
            </Button>
            {!canAddCurrent && <p className="text-xs mt-2">Select a fabric first.</p>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-separate border-spacing-0" data-testid="compare-table">
                <thead>
                  <tr>
                    <th className="text-left text-xs uppercase tracking-wider text-neutral-400 font-medium p-2 w-32">Design</th>
                    {designs.map((d, i) => (
                      <th key={d.id} className="p-2 text-left align-top" data-testid={`compare-col-${i}`}>
                        <div className="flex gap-3">
                          <div className="w-14 h-14 rounded-lg border border-neutral-200 overflow-hidden flex-shrink-0" style={{ backgroundColor: d.fabric?.hex || '#f5f5f4' }}>
                            {d.fabric?.image_url && <img src={resolveImageUrl(d.fabric.image_url, 128)} alt={d.fabric.name} className="w-full h-full object-cover" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-neutral-900 truncate" data-testid={`compare-fabric-${i}`}>{d.fabric?.name || 'No fabric'}</p>
                            <p className="text-xs text-neutral-500 font-normal truncate">{d.fabric?.path}</p>
                            <p className="text-[11px] text-neutral-400 font-normal">{new Date(d.saved_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </th>
                    ))}
                    {designs.length < MAX_COMPARE && (
                      <th className="p-2 align-top">
                        <Button variant="outline" onClick={onAddCurrent} disabled={!canAddCurrent} className="w-full h-14 border-dashed gap-1.5 text-neutral-600" data-testid="compare-add-current">
                          <Plus className="w-4 h-4" /> Add current
                        </Button>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row} className={differs(row) ? 'bg-amber-50/60' : ''}>
                      <td className="p-2 text-neutral-500 border-t border-neutral-100">{row}</td>
                      {designs.map((d, i) => (
                        <td key={d.id} className="p-2 border-t border-neutral-100 text-neutral-800" data-testid={`compare-${row.toLowerCase().replace(/\s+/g, '-')}-${i}`}>{d.rows?.[row] || '—'}</td>
                      ))}
                      {designs.length < MAX_COMPARE && <td className="border-t border-neutral-100" />}
                    </tr>
                  ))}
                  <tr>
                    <td className="p-2 text-neutral-500 border-t border-neutral-200 font-medium">Total</td>
                    {designs.map((d, i) => (
                      <td key={d.id} className={`p-2 border-t border-neutral-200 font-mono text-lg ${cheapest !== null && d.total_value === cheapest ? 'text-emerald-700' : 'text-neutral-900'}`} data-testid={`compare-total-${i}`}>
                        {d.total || 'Incomplete'}
                        {cheapest !== null && d.total_value === cheapest && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Lowest</span>}
                      </td>
                    ))}
                    {designs.length < MAX_COMPARE && <td className="border-t border-neutral-200" />}
                  </tr>
                  <tr>
                    <td className="p-2" />
                    {designs.map((d, i) => (
                      <td key={d.id} className="p-2">
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => onLoad(d)} className="bg-neutral-900 hover:bg-neutral-800 gap-1" data-testid={`compare-load-${i}`}><Upload className="w-3.5 h-3.5" /> Load</Button>
                          <Button size="sm" variant="ghost" onClick={() => onRemove(d.id)} className="text-neutral-500 hover:text-red-600" data-testid={`compare-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    ))}
                    {designs.length < MAX_COMPARE && <td />}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Quote From Compare */}
            {!showQuoteForm ? (
              <div className="flex justify-end pt-2 border-t border-neutral-100">
                <Button onClick={() => setShowQuoteForm(true)} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="compare-request-quote-btn">
                  <FileText className="w-4 h-4" /> Request quote for all {designs.length} design{designs.length > 1 ? 's' : ''}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleQuoteSubmit} className="border-t border-neutral-200 pt-4 space-y-3" data-testid="compare-quote-form">
                <p className="text-sm font-medium text-neutral-700">Request a quote for {designs.length} compared design{designs.length > 1 ? 's' : ''}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cq-name">Name *</Label>
                    <Input id="cq-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required data-testid="compare-quote-name" />
                  </div>
                  <div>
                    <Label htmlFor="cq-email">Email *</Label>
                    <Input id="cq-email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required data-testid="compare-quote-email" />
                  </div>
                  <div>
                    <Label htmlFor="cq-phone">Phone</Label>
                    <Input id="cq-phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} data-testid="compare-quote-phone" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cq-notes">Notes</Label>
                  <Textarea id="cq-notes" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Any additional details..." data-testid="compare-quote-notes" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowQuoteForm(false)} data-testid="compare-quote-cancel">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="compare-quote-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {submitting ? 'Submitting...' : 'Submit quote request'}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompareDesignsDialog;
